import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "../../api/axiosInstance";
import { useOnboardingFlow } from "../OnboardingFlowContext";
import { getRoomsMessages } from "../roomsMessages";
import {
  buildCountsByCategory,
  buildGeneratedRows,
  buildRowsPayload,
  normalizeCountInput,
  normalizeIncomingRows,
  parseCount,
  roomHasContent,
  rowsHaveRequiredRoomNumbers,
} from "../roomsDraftShared";
import { usePmsTheme } from "../../Components/PmsThemeContext";
import { getOnboardingMessages } from "../onboardingI18n";
import "../OnboardingApp.css";

export default function RoomsCountsStep() {
  const { languageId } = usePmsTheme();
  const {
    clearStepCommandState,
    markStepComplete,
    markStepIncomplete,
    setStepCommandState,
    steps,
  } = useOnboardingFlow();
  const onboardingCopy = getOnboardingMessages(languageId);
  const copy = getRoomsMessages(languageId).setup;
  const { generateConfirmTrim, generateSuccess, saveError } = copy;
  const [categories, setCategories] = useState([]);
  const [rows, setRows] = useState([]);
  const [countsByCategory, setCountsByCategory] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [feedback, setFeedback] = useState({ type: "", message: "" });
  const stepNumber = useMemo(
    () => Math.max(steps.findIndex((step) => step.id === "rooms-counts") + 1, 1),
    [steps]
  );

  const loadDraft = useCallback(async () => {
    setLoading(true);
    setFeedback({ type: "", message: "" });

    try {
      const response = await axios.get("/api/onboarding/rooms");
      const incomingCategories = Array.isArray(response.data?.categories) ? response.data.categories : [];
      const incomingRows = normalizeIncomingRows(response.data?.rows || []);

      setCategories(incomingCategories);
      setRows(incomingRows);
      setCountsByCategory(buildCountsByCategory(incomingCategories, incomingRows));
      setIsDirty(false);

      if (incomingRows.length > 0) {
        markStepComplete("rooms-counts");
      } else {
        markStepIncomplete("rooms-counts");
      }

      if (rowsHaveRequiredRoomNumbers(incomingRows)) {
        markStepComplete("rooms-grid");
      } else {
        markStepIncomplete("rooms-grid");
      }
    } catch (error) {
      console.error("[Onboarding][RoomsCounts] Error loading draft:", error);
      setCategories([]);
      setRows([]);
      setCountsByCategory({});
      setFeedback({
        type: "error",
        message: copy.loadError,
      });
    } finally {
      setLoading(false);
    }
  }, [copy.loadError, markStepComplete, markStepIncomplete]);

  useEffect(() => {
    loadDraft();
  }, [loadDraft]);

  const totalRooms = useMemo(
    () => Object.values(countsByCategory).reduce((sum, value) => sum + parseCount(value), 0),
    [countsByCategory]
  );

  const updateCount = (categoryId, value) => {
    setIsDirty(true);
    setCountsByCategory((current) => ({
      ...current,
      [categoryId]: normalizeCountInput(value),
    }));
  };

  const handleSave = useCallback(async () => {
    const rowsToTrim = categories.reduce((sum, category) => {
      const currentRows = rows.filter(
        (row) => Number(row.onboarding_room_category_id) === Number(category.id)
      );
      const targetCount = parseCount(countsByCategory[category.id]);

      if (currentRows.length <= targetCount) {
        return sum;
      }

      return sum + currentRows.slice(targetCount).filter((room) => roomHasContent(room)).length;
    }, 0);

    if (rowsToTrim > 0 && !window.confirm(generateConfirmTrim(rowsToTrim))) {
      return false;
    }

    const nextRows = buildGeneratedRows(categories, rows, countsByCategory);
    setSaving(true);
    setFeedback({ type: "", message: "" });

    try {
      const response = await axios.put("/api/onboarding/rooms", {
        rows: buildRowsPayload(nextRows),
      });
      const savedRows = normalizeIncomingRows(response.data?.rows || []);

      setRows(savedRows);
      setCountsByCategory(buildCountsByCategory(categories, savedRows));
      setIsDirty(false);

      if (savedRows.length > 0) {
        markStepComplete("rooms-counts");
      } else {
        markStepIncomplete("rooms-counts");
      }

      if (rowsHaveRequiredRoomNumbers(savedRows)) {
        markStepComplete("rooms-grid");
      } else {
        markStepIncomplete("rooms-grid");
      }

      setFeedback({
        type: "success",
        message: generateSuccess,
      });

      return true;
    } catch (error) {
      console.error("[Onboarding][RoomsCounts] Error generating rows:", error);
      setFeedback({
        type: "error",
        message: error?.response?.data?.error || saveError,
      });
      return false;
    } finally {
      setSaving(false);
    }
  }, [
    categories,
    countsByCategory,
    generateConfirmTrim,
    generateSuccess,
    markStepComplete,
    markStepIncomplete,
    rows,
    saveError,
  ]);

  useEffect(() => {
    setStepCommandState("rooms-counts", {
      onSave: handleSave,
      canSave: !loading,
      canProceed: !isDirty,
      isDirty,
      isSaving: saving,
    });

    return () => {
      clearStepCommandState("rooms-counts");
    };
  }, [clearStepCommandState, handleSave, isDirty, loading, saving, setStepCommandState]);

  if (loading) {
    return (
      <div className="onboarding-emptyState">
        <h3 className="onboarding-sectionTitle">{copy.title}</h3>
        <p className="onboarding-sectionText">{copy.copy}</p>
      </div>
    );
  }

  if (!categories.length) {
    return (
      <div className="onboarding-emptyState onboarding-formCard--centered">
        <div className="onboarding-eyebrow">{copy.eyebrow}</div>
        <h3 className="onboarding-sectionTitle">{copy.noCategoriesTitle}</h3>
        <p className="onboarding-sectionText">{copy.noCategoriesCopy}</p>
      </div>
    );
  }

  return (
    <div className="onboarding-grid">
      <section className="onboarding-formCard">
        <div className="onboarding-formHeader">
          <div>
            <div className="onboarding-eyebrow">{copy.eyebrow}</div>
            <div className="onboarding-stepCount">{onboardingCopy.progress.stepCounter(stepNumber, steps.length)}</div>
            <h3 className="onboarding-sectionTitle">{copy.title}</h3>
            <p className="onboarding-sectionText">{copy.copy}</p>
          </div>
        </div>

        <div className="onboarding-roomPlanner">
          <div className="onboarding-roomPlannerHead">
            <div>
              <h4 className="onboarding-roomPlannerTitle">{copy.title}</h4>
              <p className="onboarding-hint">{copy.copy}</p>
            </div>
            <div className="onboarding-roomPlannerTotal">
              <span>{copy.totalLabel}</span>
              <strong>{totalRooms}</strong>
            </div>
          </div>

          <div className="onboarding-roomCountGrid">
            {categories.map((category) => (
              <article key={category.id} className="onboarding-roomCountCard">
                <div className="onboarding-roomCountCardHead">
                  <div>
                    <h4>{category.name}</h4>
                    {category.description ? <p className="onboarding-hint">{category.description}</p> : null}
                  </div>
                  <span className="onboarding-stepState">{parseCount(countsByCategory[category.id])}</span>
                </div>

                <label className="onboarding-field">
                  <span className="onboarding-label">{copy.countLabel}</span>
                  <input
                    className="onboarding-input"
                    type="number"
                    min="0"
                    value={countsByCategory[category.id] ?? ""}
                    onChange={(event) => updateCount(category.id, event.target.value)}
                  />
                </label>
              </article>
            ))}
          </div>
        </div>

        {feedback.message ? (
          <div
            className={`onboarding-status ${
              feedback.type === "success" ? "onboarding-status--success" : "onboarding-status--error"
            }`}
          >
            {feedback.message}
          </div>
        ) : null}
      </section>

      <aside className="onboarding-infoCard">
        <div className="onboarding-eyebrow">{copy.eyebrow}</div>
        <h3 className="onboarding-sectionTitle">{copy.summaryTitle}</h3>
        <p className="onboarding-sectionText">{copy.summaryIntro}</p>

        <div className="onboarding-pointsList">
          {copy.summaryPoints.map((point) => (
            <div key={point} className="onboarding-pointItem">
              <span className="onboarding-pointDot" />
              <span>{point}</span>
            </div>
          ))}
        </div>
      </aside>
    </div>
  );
}
